import { observer } from 'mobx-react'
import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Text, TouchableOpacity } from 'react-native'
import { routes, routes2 } from '@linqpal/models'
import ApplicationStore from '../../../GeneralApplication/Application/ApplicationStore'
import { useInvoiceDetailsContext } from '../../TabInvoice/InvoiceDetailsContext'
import LocalStorage from '../../../../store/LocalStorage'
import { dispatcher, pathFactory } from '../../../links'
import { useResponsive } from '../../../../utils/hooks'
import {
  MESSAGE_TYPE,
  useMessage,
} from '../../../../utils/helpers/MessageProvider'
import RootStore, { useStore } from '../../../../store'
import _ from 'lodash'
import { InsufficientFundsError } from '@linqpal/models/src/types/exceptions'

type PaymentButtonProps = {
  invoices: any[]
  account?: any
  method?: any
  navigation: any
  disabled: boolean
  closePayModal: () => void
  payWithCredit?: boolean
  builderCreatedInvoice?: boolean
  loading: boolean
  showBtnOnDesktop?: boolean
}

interface StatusProps extends Status {
  setPaymentStatus?: (a: Status) => void
}

interface Status {
  status?: boolean | string
  message?: string
}

export const PaymentButton: React.FC<PaymentButtonProps> = observer(
  ({
    invoices,
    account,
    method,
    navigation,
    disabled,
    closePayModal,
    payWithCredit,
    builderCreatedInvoice,
  }): JSX.Element => {
    const { t } = useTranslation(['errors', 'global'])
    const {
      setPaymentProcessingModal,
      setInviteSuccessModal,
      setInvoiceStatusModalVisibility,
      setInvoicePaymentStatus,
      setInvoicePaymentMessage,
      setInvoicePaymentMethod,
    } = useInvoiceDetailsContext()
    const { submitCreditApplication, payAndSubmitApplication } =
      ApplicationStore
    const { userStore, screensStore } = useStore()
    const [{ status: paymentStatus = 'loading' }] = useState<StatusProps>({})
    const { setMessage } = useMessage()
    const swipeable = useRef<any>()
    const { sm } = useResponsive()
    useCallback(() => {
      _.isFunction(swipeable.current?.close) && swipeable.current?.close()

      if (paymentStatus === 'success') {
        navigation.navigate('Home', {
          screen: 'ContractorConsole',
          params: { screen: 'Invoice', params: { screen: 'InvoiceHome' } },
        })
      } else {
        navigation.goBack()
      }
    }, [swipeable, paymentStatus, navigation])

    const createLoanApplication = () => {
      LocalStorage.get('payment').then((data) => {
        if (!data.invoiceDetails?.paymentPlan)
          setMessage(
            MESSAGE_TYPE.ERROR,
            t('errors:error/no-payment-plan-selected'),
          )
        else {
          routes2.user
            .loanApplication(data.invoiceDetails)
            .then((res) => {
              routes.contractor.callDecisionEngine(res.id).then(() => {
                console.log('UW started')
                if (builderCreatedInvoice) {
                  routes.invoices
                    .inviteSupplierSchedulePay(
                      data.invoiceDetails.invoiceId,
                      userStore?.user?.sub,
                      'credit',
                    )
                    .then(() => {
                      setInvoicePaymentMessage(true)
                    })
                } else {
                  setInvoicePaymentMessage(true)
                }
              })
            })
            .catch((err) => {
              console.log(err)
            })
        }
      })
    }
    const onPayWithCredit = () => {
      _.isFunction(swipeable.current?.close) && swipeable.current?.close()
      closePayModal()
      if (payAndSubmitApplication) {
        submitCreditApplication(
          userStore.document?.loanApplicationId || '',
          setInvoicePaymentMessage,
          navigation,
        )
      } else {
        createLoanApplication()
      }
    }
    const onPay = () => {
      closePayModal()
      setInvoicePaymentMethod(account)
      setPaymentProcessingModal(true)
      setInvoicePaymentStatus('')

      if (builderCreatedInvoice) {
        routes.invoices
          .inviteSupplierSchedulePay(
            [invoices[0]._id],
            userStore?.user?.sub,
            account.id,
          )
          .then(() => {
            setPaymentProcessingModal(false)
            setInviteSuccessModal(true)
          })
      } else {
        const invoiceIds = invoices.map((invoice) => invoice._id)
        routes2.invoice
          .pay({
            invoiceIds,
            paymentMethod: method,
            accountId:
              account.paymentMethodType === 'card'
                ? account.cardMetadata.accountId
                : account.id,
            account_id: account.id,
          })
          .then(() => {
            screensStore.notificationStore.fetchNotifications()
            setPaymentProcessingModal(false)
            setInvoicePaymentStatus({ status: 'success' } as any)
            setInvoiceStatusModalVisibility(true)
            setTimeout(() => {
              setInvoiceStatusModalVisibility(false)
              const action = dispatcher(
                pathFactory('Console.InvoicesToPay.Listing'),
              )
              navigation.replace(action.name, action.params)
            }, 4000)
          })
          .catch((e) => {
            console.log('e', e)
            setPaymentProcessingModal(false)
            const result =
              e.response?.data?.result || t('errors:system-failure')

            const status =
              result === InsufficientFundsError.name ||
              e.result === InsufficientFundsError.name
                ? t('errors:insufficient-funds')
                : result === 'LogicalError'
                ? t('errors:error-occurred')
                : t('errors:system-failure')

            const dataMessage =
              e.response?.data?.message ||
              e.data?.message ||
              e.message ||
              e.toString()

            setInvoicePaymentStatus({ status, message: dataMessage } as any)
            setInvoiceStatusModalVisibility(true)
          })
      }
    }

    return (
      <TouchableOpacity
        style={{
          width: sm ? 335 : 285,
          maxWidth: 335,
          height: 44,
          backgroundColor: '#00A0F3',
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
          opacity: disabled ? 0.5 : 1,
          borderRadius: 4,
        }}
        disabled={disabled || RootStore.isBusy}
        activeOpacity={0.8}
        onPress={payWithCredit ? onPayWithCredit : onPay}
      >
        <Text
          style={{
            fontWeight: '700',
            fontSize: 16,
            lineHeight: 24,
            color: '#FFF',
          }}
        >
          {t('global:payInvoice.agree-and-pay')}
        </Text>
      </TouchableOpacity>
    )
  },
)
